import type { ReactNode } from "react";
import {
  BriefcaseBusiness,
  GraduationCap,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { CredentialType } from "@api/shared-runtime/enums";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Credential } from "@/features/credentials/types";
import { CredentialStatusColumn } from "@/features/credentials/components/credential-card/credential-status-column";
import { asDetails } from "@/features/credentials/components/credential-card/credential-details/detail-values";
import {
  chronologicalCredentials,
  licenseTitle,
  profileDateRange,
  profileText,
  profileYear,
} from "./profile-record-values";

function ProfileSection({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight text-slate-900 sm:text-xl">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function EmptyRecord({
  icon,
  children,
  onAdd,
  addLabel,
}: {
  icon: ReactNode;
  children: ReactNode;
  onAdd: () => void;
  addLabel: string;
}) {
  return (
    <div className="flex flex-col items-start gap-4 rounded-2xl border border-dashed border-slate-200 bg-white p-5 sm:flex-row sm:items-center sm:p-6">
      <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm leading-6 text-slate-500">{children}</p>
        <Button
          variant="outline"
          className="mt-4 gap-2 text-sm font-semibold text-primary hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
          onClick={onAdd}
        >
          <Plus className="size-4" aria-hidden="true" />
          {addLabel}
        </Button>
      </div>
    </div>
  );
}

export function ProfileLicenses({
  credentials,
  onOpen,
  onAdd,
}: {
  credentials: Credential[];
  onOpen: () => void;
  onAdd: () => void;
}) {
  const licenses = credentials.filter(
    (credential) =>
      credential.present &&
      [
        CredentialType.StateLicense,
        CredentialType.Dea,
        CredentialType.Cds,
      ].includes(credential.credentialType),
  );
  return (
    <ProfileSection
      title="Licenses & Registrations"
      action={
        licenses.length > 0 && (
          <Button
            variant="link"
            className="gap-2 px-3 text-sm font-semibold"
            onClick={onAdd}
          >
            <Plus className="size-4" aria-hidden="true" />
            Add New License
          </Button>
        )
      }
    >
      {licenses.length === 0 ? (
        <EmptyRecord
          icon={<ShieldCheck className="size-6" aria-hidden="true" />}
          onAdd={onAdd}
          addLabel="Add license"
        >
          Your licenses and registrations will appear here once they’re on file.
        </EmptyRecord>
      ) : (
        <div className="space-y-3">
          {licenses.map((credential) => (
            <button
              type="button"
              key={credential.id}
              onClick={onOpen}
              className="group flex w-full flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:p-6"
            >
              <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary transition-colors group-hover:bg-primary/10">
                <ShieldCheck className="size-6" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block break-words text-base font-semibold text-slate-900 sm:text-lg">
                  {licenseTitle(credential)}
                </span>
                <span className="mt-1 block break-words text-sm leading-6 text-slate-500">
                  {[
                    credential.label,
                    credential.version?.number &&
                      `#${credential.version.number}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <div className="w-full min-w-0 pl-16 [&>div]:items-start sm:ml-auto sm:w-auto sm:pl-0 sm:[&>div]:items-end">
                <CredentialStatusColumn credential={credential} />
              </div>
            </button>
          ))}
        </div>
      )}
    </ProfileSection>
  );
}

export function ProfileWorkHistory({
  credentials,
  onAdd,
}: {
  credentials: Credential[];
  onAdd: () => void;
}) {
  const history = chronologicalCredentials(
    credentials.filter(
      (credential) =>
        credential.present &&
        credential.credentialType === CredentialType.WorkHistory,
    ),
  );
  return (
    <ProfileSection title="Work History">
      {history.length === 0 ? (
        <EmptyRecord
          icon={<BriefcaseBusiness className="size-6" aria-hidden="true" />}
          onAdd={onAdd}
          addLabel="Add work history"
        >
          Your professional experience will appear here once it’s on file.
        </EmptyRecord>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
          <ol className="ml-2 space-y-7 border-l-2 border-slate-200 pl-7 sm:pl-9">
            {history.map((credential) => {
              const details = asDetails(credential.version?.details);
              const isCurrent = details.currentlyEmployed === true;
              const isGap = details.kind === "gap";
              const title =
                profileText(details.employerName) ??
                (isGap ? "Career break" : (credential.summary ?? "Employment"));
              const dates = profileDateRange({
                startDate: details.startDate,
                endDate: details.endDate,
                current: isCurrent,
              });
              const description = profileText(
                isGap ? details.gapExplanation : details.responsibilities,
              );
              return (
                <li key={credential.id} className="relative">
                  <span
                    className={cn(
                      "absolute -left-[37px] top-2 size-4 rounded-full ring-4 ring-white sm:-left-[45px]",
                      isCurrent ? "bg-primary" : "bg-slate-300",
                    )}
                    aria-hidden="true"
                  />
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="break-words text-base font-semibold text-slate-900 sm:text-lg">
                        {title}
                      </h3>
                      {profileText(details.position) && (
                        <p
                          className={cn(
                            "mt-1 text-sm font-medium",
                            isCurrent ? "text-primary" : "text-slate-600",
                          )}
                        >
                          {profileText(details.position)}
                        </p>
                      )}
                    </div>
                    {dates && (
                      <span className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
                        {dates}
                      </span>
                    )}
                  </div>
                  {description && (
                    <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-500">
                      {description}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </ProfileSection>
  );
}

export function ProfileEducation({
  credentials,
  onOpen,
  onAdd,
}: {
  credentials: Credential[];
  onOpen: () => void;
  onAdd: () => void;
}) {
  const education = chronologicalCredentials(
    credentials.filter(
      (credential) =>
        credential.present &&
        [CredentialType.Education, CredentialType.Training].includes(
          credential.credentialType,
        ),
    ),
  );
  return (
    <ProfileSection title="Education & Schooling">
      {education.length === 0 ? (
        <EmptyRecord
          icon={<GraduationCap className="size-6" aria-hidden="true" />}
          onAdd={onAdd}
          addLabel="Add education"
        >
          Your education and training will appear here once they’re on file.
        </EmptyRecord>
      ) : (
        <div className="space-y-3">
          {education.map((credential) => {
            const details = asDetails(credential.version?.details);
            const year = profileYear(
              details.endDate ?? credential.version?.issuedAt,
            );
            const subtitle = [
              profileText(details.degree),
              profileText(details.trainingType),
              profileText(details.specialty),
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <button
                key={credential.id}
                type="button"
                onClick={onOpen}
                className="group flex w-full items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 sm:items-center sm:p-6"
              >
                <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary transition-colors group-hover:bg-primary/10">
                  <GraduationCap className="size-6" aria-hidden="true" />
                </span>
                <span className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-3">
                  <span className="min-w-0">
                    <span className="block break-words text-base font-semibold text-slate-900 sm:text-lg">
                      {profileText(details.institutionName) ??
                        credential.summary ??
                        credential.label}
                    </span>
                    {subtitle && (
                      <span className="mt-1 block break-words text-sm leading-6 text-slate-500">
                        {subtitle}
                      </span>
                    )}
                  </span>
                  {year && (
                    <span className="text-xs font-medium text-slate-500">
                      Class of {year}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </ProfileSection>
  );
}
